import { isUngraded } from '../designTokens';
import type { ScanResult } from './types';

// The scanner home lists a customer's audits by site rather than by run:
// one row per page they audit, with its latest result and the change since
// the audit before it. A "page" is the URL with www and a trailing slash
// folded away, so acme.com and www.acme.com/ are one row while acme.com/shop
// keeps its own (comparing a shop page's score to a homepage's would be a
// fake trend).

export interface Site {
  key: string;
  /** The page as shown: host without www, plus any path. */
  label: string;
  /** The URL the latest audit ran against; "Audit again" reuses it. */
  url: string;
  latest: ScanResult;
  auditCount: number;
  /** Score change from the previous finished, graded audit, or null. */
  scoreChange: number | null;
}

export function siteKey(url: string): string {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const path = parsed.pathname.replace(/\/+$/, '');
    return `${host}${path}`;
  } catch {
    return url;
  }
}

const isGradedFinish = (s: ScanResult) => s.status === 'completed' && !isUngraded(s.overallStatus);

export function groupScansBySite(scans: ScanResult[]): Site[] {
  const byKey = new Map<string, ScanResult[]>();
  for (const s of scans) {
    const key = siteKey(s.url);
    const list = byKey.get(key);
    if (list) list.push(s);
    else byKey.set(key, [s]);
  }

  const sites: Site[] = [];
  for (const [key, list] of byKey) {
    list.sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
    const finished = list.filter((s) => s.status === 'completed');
    const [current, previous] = finished;
    const scoreChange =
      current && previous && isGradedFinish(current) && isGradedFinish(previous)
        ? current.score - previous.score
        : null;
    sites.push({ key, label: key, url: list[0].url, latest: list[0], auditCount: list.length, scoreChange });
  }

  return sites.sort((a, b) => Date.parse(b.latest.startedAt) - Date.parse(a.latest.startedAt));
}
